import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, KeyRound, ShieldCheck, Wrench, User, Pencil } from 'lucide-react';
import { users as usersApi, auth } from '../../lib/api';
import { toast } from 'sonner';

const roleMeta = {
  admin: { label: 'Admin', icon: ShieldCheck, color: 'bg-[#8BC53F]/15 text-[#5b8523]' },
  tech: { label: 'Technician', icon: Wrench, color: 'bg-amber-100 text-amber-700' },
  customer: { label: 'Customer', icon: User, color: 'bg-slate-100 text-slate-700' },
};

export default function TeamManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    usersApi.list().then((d) => setList(d || [])).catch(() => toast.error('Could not load users (admin only)')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (u) => {
    if (!window.confirm(`Remove login for ${u.email}?`)) return;
    try { await usersApi.remove(u.email); load(); toast.success('User removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="team-manager">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-bold text-slate-900">Team & Logins</div>
          <p className="text-sm text-slate-500">{list.length} account(s). Add admins, technicians, and customer logins.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="add-user-btn"><Plus className="w-4 h-4" />Add User</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
            <tr><th className="px-4 py-2.5 text-left">Name</th><th className="px-4 py-2.5 text-left">Email</th><th className="px-4 py-2.5 text-left">Role</th><th className="px-4 py-2.5 text-left">Last Login</th><th className="px-4 py-2.5"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((u) => {
              const m = roleMeta[u.role] || roleMeta.customer;
              return (
                <tr key={u.email} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name}{u.techId ? <span className="ml-2 text-xs text-slate-400 font-mono">{u.techId}</span> : null}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><Badge className={`${m.color} border-0 gap-1`}><m.icon className="w-3 h-3" />{m.label}</Badge></td>
                  <td className="px-4 py-3 text-slate-500 text-xs" data-testid={`last-login-${u.email}`}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : <span className="text-slate-300">Never</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title="Edit" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4 text-slate-600" /></Button>
                      <Button size="sm" variant="ghost" title="Reset password" onClick={() => setPwUser(u)} data-testid={`reset-pw-${u.email}`}><KeyRound className="w-4 h-4 text-amber-600" /></Button>
                      <Button size="sm" variant="ghost" title="Delete" onClick={() => onDelete(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && list.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <UserDialog open={open} setOpen={setOpen} editing={editing} onSaved={load} />
      <ResetPwDialog user={pwUser} setUser={setPwUser} />
    </Card>
  );
}

function UserDialog({ open, setOpen, editing, onSaved }) {
  const [f, setF] = useState({ email: '', name: '', password: '', role: 'tech', techId: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setF({ email: editing.email, name: editing.name || '', password: '', role: editing.role || 'tech', techId: editing.techId || '' });
    else setF({ email: '', name: '', password: '', role: 'tech', techId: '' });
  }, [editing, open]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await usersApi.update(editing.email, { name: f.name, role: f.role, techId: f.techId || null });
        toast.success('User updated');
      } else {
        if (!f.email.trim() || !f.password) { toast.error('Email and password required'); setSaving(false); return; }
        await usersApi.create({ email: f.email.trim(), name: f.name.trim(), password: f.password, role: f.role, techId: f.role === 'customer' ? null : (f.techId || null) });
        toast.success('User created');
      }
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle><DialogDescription>{editing ? 'Update role and details.' : 'Create a login for a team member or customer.'}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Email</Label><Input value={f.email} disabled={!!editing} onChange={(e) => set('email', e.target.value)} className="mt-1.5" data-testid="user-email-input" placeholder="name@xpertpestssolutions.com" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5" data-testid="user-name-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => set('role', v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="tech">Technician</SelectItem><SelectItem value="customer">Customer</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          {f.role !== 'customer' && <div><Label>Tech ID {f.role === 'tech' ? '(optional)' : '— only if this person also runs jobs'}</Label><Input value={f.techId} onChange={(e) => set('techId', e.target.value)} className="mt-1.5" placeholder="TECH-003" data-testid="user-techid-input" />{f.role === 'admin' && <p className="text-xs text-slate-500 mt-1">An admin with a Tech ID keeps full access and also gets jobs assigned and their own My Day in the tech app.</p>}</div>}
          {!editing && <div><Label>Temporary Password</Label><Input type="text" value={f.password} onChange={(e) => set('password', e.target.value)} className="mt-1.5" data-testid="user-password-input" placeholder="min 6 characters" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="user-save-btn">{saving ? 'Saving…' : editing ? 'Save' : 'Create User'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwDialog({ user, setUser }) {
  const [pw, setPw] = useState('');
  useEffect(() => { setPw(''); }, [user]);
  const submit = async () => {
    if (pw.length < 6) { toast.error('Min 6 characters'); return; }
    try { await usersApi.setPassword(user.email, pw); toast.success(`Password reset for ${user.email}`); setUser(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && setUser(null)}>
      <DialogContent data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>{user?.email}</DialogDescription></DialogHeader>
        <div><Label>New Password</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" data-testid="reset-pw-input" placeholder="min 6 characters" /></div>
        <DialogFooter><Button variant="outline" onClick={() => setUser(null)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="reset-pw-save">Reset Password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangePasswordCard() {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!cur || nw.length < 6) { toast.error('Enter current password and a new password (min 6)'); return; }
    setSaving(true);
    try { await auth.changePassword(cur, nw); toast.success('Password changed'); setCur(''); setNw(''); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 border-slate-200" data-testid="change-password-card">
      <div className="font-bold text-slate-900 mb-1">Change My Password</div>
      <p className="text-sm text-slate-500 mb-4">Update the password for your own account.</p>
      <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
        <div><Label>Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" data-testid="current-pw-input" /></div>
        <div><Label>New Password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1.5" data-testid="new-pw-input" /></div>
      </div>
      <Button onClick={submit} disabled={saving} className="mt-4 bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="change-pw-btn">{saving ? 'Saving…' : 'Update Password'}</Button>
    </Card>
  );
}
